import {
  useCallback,
  useMemo,
  type CSSProperties,
  type KeyboardEventHandler,
  type PointerEventHandler,
  type ReactNode,
} from 'react';
import type {
  Data,
  DraggableAttributes,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { useSortable, type UseSortableArguments } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface SortableItemRenderProps {
  /** Ref for the element that moves (the row / list item). */
  setNodeRef: (element: HTMLElement | null) => void;
  /** Ref for the drag handle; keyboard drags only start from this element. */
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  /** Ref for an element that both moves and acts as its own drag handle. */
  setHandleNodeRef: (element: HTMLElement | null) => void;
  /** Spread on the drag handle together with `listeners`. */
  attributes: DraggableAttributes;
  /**
   * Drag-start handlers for the drag handle. The keyboard handler runs in the
   * capture phase so components that call `preventDefault()` on Space in their
   * own key handlers (e.g. MUI `ListItemButton`) cannot block keyboard drags,
   * matching the previous library's capture-phase listeners.
   */
  listeners: SortableHandleListeners | undefined;
  /** Translate/transition style for the moving element. */
  style: CSSProperties;
  isDragging: boolean;
}

export interface SortableHandleListeners {
  onPointerDown?: PointerEventHandler<HTMLElement>;
  onKeyDownCapture?: KeyboardEventHandler<HTMLElement>;
}

export interface SortableItemProps {
  id: UniqueIdentifier;
  /** `true` disables dragging and dropping; pass an object to split them. */
  disabled?: UseSortableArguments['disabled'];
  /** Extra data exposed as `active.data.current` / `over.data.current`. */
  data?: Data;
  /** ARIA role for the drag handle attributes (defaults to `button`). */
  role?: string;
  children: (props: SortableItemRenderProps) => ReactNode;
}

/**
 * Render-prop sortable item so class components can opt into `useSortable`
 * the same way they consumed the previous library's `<Draggable>`.
 */
export const SortableItem = ({
  id,
  disabled = false,
  data,
  role,
  children,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled,
    data,
    attributes: role ? { role } : undefined,
  });

  const handleListeners = useMemo<SortableHandleListeners | undefined>(
    () =>
      listeners && {
        onPointerDown:
          listeners.onPointerDown as PointerEventHandler<HTMLElement>,
        onKeyDownCapture:
          listeners.onKeyDown as KeyboardEventHandler<HTMLElement>,
      },
    [listeners],
  );

  const setHandleNodeRef = useCallback(
    (element: HTMLElement | null) => {
      setNodeRef(element);
      setActivatorNodeRef(element);
    },
    [setNodeRef, setActivatorNodeRef],
  );

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    // Keep the lifted item above its siblings while it moves.
    ...(isDragging ? { position: 'relative', zIndex: 1 } : {}),
  };

  return (
    <>
      {children({
        setNodeRef,
        setActivatorNodeRef,
        setHandleNodeRef,
        attributes,
        listeners: handleListeners,
        style,
        isDragging,
      })}
    </>
  );
};
